'use client';

import { useState } from 'react';
import { toast } from 'sonner';
import { FileCheck2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { submitForensic } from '@/lib/actions/public';
import type { Locale } from '@/lib/i18n/config';
import type { Dictionary } from '@/lib/i18n/dictionaries';

export function ForensicForm({ locale, dict }: { locale: Locale; dict: Dictionary }) {
  const [loading, setLoading] = useState(false);
  const [requestType, setRequestType] = useState<string>('');

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    setLoading(true);
    const result = await submitForensic({
      requester_name: String(fd.get('requester_name') ?? ''),
      institution: String(fd.get('institution') ?? ''),
      court_case: String(fd.get('court_case') ?? ''),
      request_type: requestType,
      email: String(fd.get('email') ?? ''),
      phone: String(fd.get('phone') ?? ''),
      message: String(fd.get('message') ?? ''),
      locale,
    });
    setLoading(false);
    if (result.ok) {
      toast.success(dict.request.success);
      form.reset();
      setRequestType('');
    } else {
      toast.error(dict.request.error);
    }
  }

  const types = dict.request.requestTypes;

  return (
    <form onSubmit={onSubmit} className="grid gap-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={dict.request.requesterName} name="requester_name" autoComplete="name" required />
        <Field label={dict.request.institution} name="institution" autoComplete="organization" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={dict.request.courtCase} name="court_case" />
        <div>
          <Label htmlFor="request-type" className="mb-1.5 block text-sm font-medium">{dict.request.requestType}</Label>
          <Select value={requestType} onValueChange={setRequestType}>
            <SelectTrigger id="request-type">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="voice">{types.voice}</SelectItem>
              <SelectItem value="audio">{types.audio}</SelectItem>
              <SelectItem value="video">{types.video}</SelectItem>
              <SelectItem value="linguistic">{types.linguistic}</SelectItem>
              <SelectItem value="face">{types.face}</SelectItem>
              <SelectItem value="other">{types.other}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={dict.request.email} name="email" type="email" autoComplete="email" required />
        <Field label={dict.request.phone} name="phone" autoComplete="tel" />
      </div>
      <div>
        <Label htmlFor="message" className="mb-1.5 block text-sm font-medium">{dict.request.message}<span className="text-accent"> *</span></Label>
        <Textarea id="message" name="message" rows={6} required className="resize-none" />
      </div>
      <Button
        type="submit"
        disabled={loading}
        className="bg-accent text-accent-foreground hover:bg-accent/90"
      >
        <FileCheck2 className="mr-2 h-4 w-4" />
        {loading ? dict.common.loading : dict.request.submit}
      </Button>
    </form>
  );
}

function Field({ label, name, type = 'text', required = false, autoComplete }: { label: string; name: string; type?: string; required?: boolean; autoComplete?: string }) {
  return (
    <div>
      <Label htmlFor={name} className="mb-1.5 block text-sm font-medium">{label}{required && <span className="text-accent"> *</span>}</Label>
      <Input id={name} name={name} type={type} required={required} autoComplete={autoComplete} />
    </div>
  );
}
